import type { Platform } from './types';

export const platforms: Platform[] = [
  // Advertising Platforms
  {
    id: 'google-ads',
    name: 'Google Ads',
    description: 'Search, Display, YouTube, and Shopping campaigns',
    category: 'advertising',
    metrics: [
      { key: 'impressions', label: 'Impressions', format: 'number' },
      { key: 'clicks', label: 'Clicks', format: 'number' },
      { key: 'ctr', label: 'CTR', format: 'percentage' },
      { key: 'spend', label: 'Spend', format: 'currency' },
      { key: 'conversions', label: 'Conversions', format: 'number' },
      { key: 'cost_per_conversion', label: 'Cost per Conversion', format: 'currency' },
    ],
  },
  {
    id: 'meta-ads',
    name: 'Meta Ads Manager',
    description: 'Facebook and Instagram ad campaigns',
    category: 'advertising',
    metrics: [
      { key: 'reach', label: 'Reach', format: 'number' },
      { key: 'impressions', label: 'Impressions', format: 'number' },
      { key: 'clicks', label: 'Clicks', format: 'number' },
      { key: 'ctr', label: 'CTR', format: 'percentage' },
      { key: 'spend', label: 'Spend', format: 'currency' },
      { key: 'conversions', label: 'Conversions', format: 'number' },
    ],
  },
  {
    id: 'tiktok-ads',
    name: 'TikTok Ads',
    description: 'TikTok advertising campaigns and analytics',
    category: 'advertising',
    metrics: [
      { key: 'video_views', label: 'Video Views', format: 'number' },
      { key: 'impressions', label: 'Impressions', format: 'number' },
      { key: 'engagement_rate', label: 'Engagement Rate', format: 'percentage' },
      { key: 'spend', label: 'Spend', format: 'currency' },
      { key: 'conversions', label: 'Conversions', format: 'number' },
      { key: 'cost_per_view', label: 'Cost per View', format: 'currency' },
    ],
  },
  {
    id: 'linkedin-ads',
    name: 'LinkedIn Ads',
    description: 'Professional network advertising',
    category: 'advertising',
    metrics: [
      { key: 'impressions', label: 'Impressions', format: 'number' },
      { key: 'clicks', label: 'Clicks', format: 'number' },
      { key: 'ctr', label: 'CTR', format: 'percentage' },
      { key: 'spend', label: 'Spend', format: 'currency' },
      { key: 'leads', label: 'Leads', format: 'number' },
      { key: 'cost_per_lead', label: 'Cost per Lead', format: 'currency' },
    ],
  },
  {
    id: 'twitter-ads',
    name: 'Twitter Ads',
    description: 'Twitter advertising and promoted content',
    category: 'advertising',
    metrics: [
      { key: 'impressions', label: 'Impressions', format: 'number' },
      { key: 'engagements', label: 'Engagements', format: 'number' },
      { key: 'engagement_rate', label: 'Engagement Rate', format: 'percentage' },
      { key: 'spend', label: 'Spend', format: 'currency' },
      { key: 'follows', label: 'Follows', format: 'number' },
      { key: 'cost_per_follow', label: 'Cost per Follow', format: 'currency' },
    ],
  },
  // Sales Platforms
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'E-commerce platform for online stores',
    category: 'sales',
    metrics: [
      { key: 'total_orders', label: 'Total Orders', format: 'number' },
      { key: 'revenue', label: 'Revenue', format: 'currency' },
      { key: 'average_order_value', label: 'Average Order Value', format: 'currency' },
      { key: 'conversion_rate', label: 'Conversion Rate', format: 'percentage' },
      { key: 'returning_customers', label: 'Returning Customers', format: 'percentage' },
      { key: 'cart_abandonment_rate', label: 'Cart Abandonment Rate', format: 'percentage' },
    ],
  },
  {
    id: 'amazon',
    name: 'Amazon Seller Central',
    description: 'Amazon marketplace selling platform',
    category: 'sales',
    metrics: [
      { key: 'total_sales', label: 'Total Sales', format: 'currency' },
      { key: 'units_sold', label: 'Units Sold', format: 'number' },
      { key: 'buy_box_percentage', label: 'Buy Box Percentage', format: 'percentage' },
      { key: 'feedback_rating', label: 'Feedback Rating', format: 'percentage' },
      { key: 'refund_rate', label: 'Refund Rate', format: 'percentage' },
      { key: 'fba_revenue', label: 'FBA Revenue', format: 'currency' },
    ],
  },
  {
    id: 'ebay',
    name: 'eBay',
    description: 'Online marketplace and auction platform',
    category: 'sales',
    metrics: [
      { key: 'total_sales', label: 'Total Sales', format: 'currency' },
      { key: 'items_sold', label: 'Items Sold', format: 'number' },
      { key: 'average_selling_price', label: 'Average Selling Price', format: 'currency' },
      { key: 'seller_rating', label: 'Seller Rating', format: 'percentage' },
      { key: 'views', label: 'Item Views', format: 'number' },
      { key: 'watch_count', label: 'Watch Count', format: 'number' },
    ],
  },
  {
    id: 'etsy',
    name: 'Etsy',
    description: 'Handmade and vintage items marketplace',
    category: 'sales',
    metrics: [
      { key: 'total_revenue', label: 'Total Revenue', format: 'currency' },
      { key: 'orders', label: 'Orders', format: 'number' },
      { key: 'visits', label: 'Shop Visits', format: 'number' },
      { key: 'conversion_rate', label: 'Conversion Rate', format: 'percentage' },
      { key: 'average_order_value', label: 'Average Order Value', format: 'currency' },
      { key: 'listing_views', label: 'Listing Views', format: 'number' },
    ],
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    description: 'WordPress e-commerce platform',
    category: 'sales',
    metrics: [
      { key: 'total_sales', label: 'Total Sales', format: 'currency' },
      { key: 'net_sales', label: 'Net Sales', format: 'currency' },
      { key: 'average_order_value', label: 'Average Order Value', format: 'currency' },
      { key: 'items_sold', label: 'Items Sold', format: 'number' },
      { key: 'refunded_items', label: 'Refunded Items', format: 'number' },
      { key: 'discounted_orders', label: 'Discounted Orders', format: 'percentage' },
    ],
  },
];

export const formatMetricValue = (value: number, format: string): string => {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
      }).format(value / 100);
    default:
      return new Intl.NumberFormat('en-US').format(value);
  }
};